import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
  ButtonBase,
  Checkbox,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";

import { TaskStatus } from "api/model";
import type { OrthoWorkflow } from "context/OrthoWorkflow";

import {
  getAnalysisCellPlan,
  isAnalysisSelected,
  type AnalysisKind,
} from "./analysisRunPlan";

/** Names the analysis in accessible labels. */
const ANALYSIS_NAMES: Record<AnalysisKind, string> = {
  canopy: "canopy analysis",
  emergence: "emergence analysis",
};

const formatCompletedDate = (iso: string | null | undefined): string | null => {
  if (iso == null) {
    return null;
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

interface AnalysisTaskCellProps {
  /** Whether the user flipped the cell from its default selection. */
  isToggled: boolean;
  kind: AnalysisKind;
  onToggle: () => void;
  orthoLabel: string;
  trialId: number;
  workflow: OrthoWorkflow | undefined;
}

/** One compact status cell: a select box, or an error icon / completed check that can be clicked to select a re-run. */
const AnalysisTaskCell: React.FC<AnalysisTaskCellProps> = ({
  isToggled,
  kind,
  onToggle,
  orthoLabel,
  trialId,
  workflow,
}) => {
  const { errorMessage, isSelectable, needsOptIn, task } = getAnalysisCellPlan(
    workflow,
    trialId,
    kind,
  );
  const isSelected = isAnalysisSelected({ needsOptIn }, isToggled);
  const name = ANALYSIS_NAMES[kind];

  // Picks the cell for a re-run; a plain `ButtonBase` keeps the result visible until then.
  const withRetry = (result: React.ReactElement): React.ReactElement =>
    isSelectable ? (
      <ButtonBase
        aria-label={`Select ${name} for ortho ${orthoLabel} to run again`}
        onClick={onToggle}
        sx={{ borderRadius: 1, p: 0.25 }}
      >
        {result}
      </ButtonBase>
    ) : (
      result
    );

  return (
    <TableCell align="center" sx={{ px: 0.5, py: 0.25 }}>
      {isSelectable && (isSelected || !needsOptIn) ? (
        <Checkbox
          checked={isSelected}
          color="primary"
          onChange={onToggle}
          size="small"
          slotProps={{
            input: { "aria-label": `Run ${name} for ortho ${orthoLabel}` },
          }}
          sx={{ p: 0.25 }}
        />
      ) : errorMessage != null ? (
        <Tooltip title={errorMessage}>
          {withRetry(
            <ErrorIcon
              aria-label={`${name} failed for ortho ${orthoLabel}`}
              color="error"
              sx={{ fontSize: 16 }}
            />,
          )}
        </Tooltip>
      ) : task.status === TaskStatus.completed ? (
        withRetry(
          <Stack
            aria-label={`${name} completed for ortho ${orthoLabel}`}
            direction="row"
            spacing={0.5}
            sx={{ alignItems: "center", justifyContent: "center" }}
          >
            <CheckCircle color="success" sx={{ fontSize: 16 }} />
            <Typography variant="caption">
              {formatCompletedDate(task.task?.completed_at) ?? ""}
            </Typography>
          </Stack>,
        )
      ) : (
        <Typography color="text.secondary" variant="caption">
          —
        </Typography>
      )}
    </TableCell>
  );
};

export default AnalysisTaskCell;
